"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Sparkles, Shield } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function FourOfPentaclesPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-green-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Pentagramlar</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Dört Pentagram</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Minör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Dört Pentagram</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 rounded-xl p-8 mb-8 border border-green-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/four-of-pentacles.svg"
                  alt="Four of Pentacles Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-700 rounded-full flex items-center justify-center">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Four of Pentacles</h1>
                  <p className="text-xl text-green-400">Dört Pentagram / Güvenlik ve Koruma</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Güvenlik, koruma ve kontrolün kartı. Mali istikrar, tutumluluk
                ve kaynaklarını koruma isteğini temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Güvenlik</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Kontrol</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Tutumluluk</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Koruma</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Mali Güvenlik</h3>
                  <p className="text-slate-400 text-sm">Finansal istikrar, birikim yapma ve gelecek güvencesi.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Tutumluluk ve Tasarruf</h3>
                  <p className="text-slate-400 text-sm">Akıllı harcama, bütçe yönetimi ve kaynakları koruma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İstikrar ve Düzen</h3>
                  <p className="text-slate-400 text-sm">Hayatta düzen kurma, planlı yaşam ve öngörülebilirlik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Koruyucu Yaklaşım</h3>
                  <p className="text-slate-400 text-sm">Sahip olunanları koruma, dikkatli davranma ve risk almama.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aşırı Cimrilik</h3>
                  <p className="text-slate-400 text-sm">Gereksiz kısıtlama, aşırı tutumluluk ve cömertlik eksikliği.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Maddi Obsesyon</h3>
                  <p className="text-slate-400 text-sm">Para ve sahiplik takıntısı, materyalizm ve açgözlülük.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yenilikten Kaçınma</h3>
                  <p className="text-slate-400 text-sm">Değişime dirençli olma, rutinlere takılma ve fırsat kaçırma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sosyal İzolasyon</h3>
                  <p className="text-slate-400 text-sm">Güvensizlik nedeniyle kendini soyutlama ve paylaşımdan kaçınma.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Koruyucu Duruş</h3>
              <p className="text-slate-400 text-sm">Savunma pozisyonu, güvenlik arayışı ve korunma.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👑</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Taç</h3>
              <p className="text-slate-400 text-sm">Statü, sosyal konum ve sahiplik hissi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🏛️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Şehir</h3>
              <p className="text-slate-400 text-sm">Medeniyet, düzen ve toplumsal yapı.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⭐</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Dört Pentagram</h3>
              <p className="text-slate-400 text-sm">Maddi kaynak, güvenlik ve kontrol altındaki varlıklar.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🤝</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kapanık Duruş</h3>
              <p className="text-slate-400 text-sm">Kendini koruma, güvensizlik ve paylaşmama.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🏠</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Güven Alanı</h3>
              <p className="text-slate-400 text-sm">Konfor alanı, güvenli sınırlar ve bilinen ortam.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Güvenli ilişki arayışı</li>
                <li>• Duygusal koruma</li>
                <li>• Yavaş yaklaşım</li>
                <li>• Sadakat ve bağlılık</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Mali disiplin</li>
                <li>• Güvenli yatırımlar</li>
                <li>• İstikrarlı gelir</li>
                <li>• Konservatif yaklaşım</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Öz disiplin geliştirme</li>
                <li>• Sınır belirleme</li>
                <li>• Sabır ve dayanıklılık</li>
                <li>• Güvenlik hissi yaratma</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-400" />
                Sağlık ve Yaşam
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Koruyucu önlemler</li>
                <li>• Düzenli alışkanlıklar</li>
                <li>• Güvenli seçimler</li>
                <li>• Stres yönetimi</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/pentacles/three-of-pentacles"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Üç Pentagram
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/pentacles/five-of-pentacles"
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Beş Pentagram
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}